// visualizations/dipole_visualizations.js
/**
 * @fileoverview Functions to display dipole moment arrows in the canvas.
 *
 * Each dipole is rendered as a two-color arrow encoding polarity:
 *   - Cyan shaft (negative end) → Magenta arrowhead (positive end)
 * The gradient makes the dipole direction instantly readable without
 * a legend, and both colors are absent from CPK atom coloring.
 *
 * @typedef {{
 *     compounds: AtomGroup[],
 *     residues: AtomGroup[],
 *     proteinResidues?: AtomGroup[],
 * }} DipoleContext
 *
 * Public interface:
 *     displayDipolesForBindingSite(show, includeWeak, context, params)
 *     removeDipolesForBindingSite()
 */
import {
    findDipoles, findBindingPocketDipole, defaultDipoleParams,
} from 'BMapsSrc/interactions/dipoles';
import {
    drawCylinder, removeCylinder, drawArrow, removeArrow,
    drawSphere, removeSphere,
} from 'BMapsSrc/MainCanvas';
import { Mul3_1, Add3_3, Sub3_3 } from 'BMapsSrc/math';
import { getFullResidueId } from 'BMapsSrc/util/mol_info_utils';
import { simpleCond } from 'BMapsSrc/util/js_utils';

// Colors: cyan (negative end) → magenta (positive end / arrowhead)
const SHAFT_COLOR = '#00E5FF';
const AGGREGATE_SHAFT_COLOR = '#FFB300'; // Gold/amber for aggregate protein dipoles
const WEAK_SHAFT_COLOR = 'lime'; // Match weak HBond color
const HEAD_COLOR = '#FF2D9B';

/**
 * Each drawn dipole is tracked as { shaft, center, head } so all
 * shapes can be cleaned up together.
 * @type {Array<{ shaft: *, center: *, head: * }>}
 */
const dipoleShapes = [];

/**
 * Display, hide, or redisplay dipole moment arrows in a binding
 * site.
 * @param {boolean} show
 * @param {boolean} includeWeak
 * @param {DipoleContext} bindingSiteContext
 * @param {object} params
 */
export function displayDipolesForBindingSite(
    show, includeWeak, bindingSiteContext,
    params = defaultDipoleParams
) {
    removeDipolesForBindingSite();

    if (show && bindingSiteContext) {
        const { compounds, residues, proteinResidues } = bindingSiteContext;
        const atomGroups = [...compounds, ...residues];
        const dipoles = findDipoles(atomGroups, params);
        // Aggregate pocket dipole uses only protein residues (not cofactors)
        const pocketResidues = proteinResidues || residues;
        dipoles.push(...findBindingPocketDipole(compounds, pocketResidues, params));
        console.log(
            `Dipoles: ${dipoles.length} found from ${compounds.length} compounds, ${residues.length} residues`
        );
        drawDipoles(dipoles, includeWeak, params);
    }
}

export function removeDipolesForBindingSite() {
    for (const { shaft, center, head } of dipoleShapes) {
        removeCylinder(shaft);
        removeSphere(center);
        removeArrow(head);
    }
    dipoleShapes.length = 0;
}

// --- internal ---

function drawDipoles(dipoles, includeWeak, params) {
    for (const dipole of dipoles) {
        dipole.isWeak = dipole.magnitude < params.moderateThreshold;
        if (!includeWeak && dipole.isWeak) continue;
        const shapes = drawOneDipole(dipole, params);
        if (shapes) dipoleShapes.push(shapes);
    }
}

function toObj([x, y, z]) { return { x, y, z }; }

// Arrow half-length scaling: Angstroms per Debye
const MAGNITUDE_SCALE = 1.0;
const MIN_HALF_LEN = 1.0;
const MAX_HALF_LEN = 10.0;

/**
 * Draw a single dipole as three shapes:
 *   1. Cyan/gold cylinder (shaft) from start to a split point
 *   2. Small sphere at center of mass
 *   3. Magenta arrow (head) from split point to end
 * The split point is chosen so the arrow's cone portion starts
 * where the shaft ends, giving a clean color transition.
 */
function drawOneDipole(dipoleInfo, params) {
    const {
        label, magnitude, direction, centerOfMass,
    } = dipoleInfo;

    // Arrow length scaled by dipole magnitude
    const halfLen = Math.min(MAX_HALF_LEN,
        Math.max(MIN_HALF_LEN, magnitude * MAGNITUDE_SCALE));
    const halfVec = Mul3_1(direction, halfLen);
    const start = Sub3_3(centerOfMass, halfVec);
    const end = Add3_3(centerOfMass, halfVec);

    const radius = 0.15;
    const radiusRatio = 2.0;
    const sphereRadius = radius * 1.5;
    // The arrowhead cone length is roughly 3× the cone radius
    // (radiusRatio * radius). Place the split so the shaft ends
    // where the cone base begins.
    const coneLen = radiusRatio * radius * 3;
    const totalLen = halfLen * 2;
    const splitFrac = Math.max(0.3, 1 - coneLen / totalLen);
    const splitPt = Add3_3(
        start, Mul3_1(Sub3_3(end, start), splitFrac)
    );

    const tooltip = dipoleTooltipInfo(dipoleInfo, params);
    const shapeOpts = { description: tooltip };
    const shaftColor = simpleCond([
        [dipoleInfo.isAggregate, AGGREGATE_SHAFT_COLOR],
        [dipoleInfo.isWeak, WEAK_SHAFT_COLOR],
        [true, SHAFT_COLOR],
    ]);

    const shaft = drawCylinder(
        toObj(start), toObj(splitPt),
        radius, shaftColor,
        false, // not dashed
        shapeOpts
    );

    // Ligand (compound/cofactor) dipoles get a magenta midpoint sphere
    // to distinguish them from protein residue dipoles
    const sphereColor = !label ? HEAD_COLOR : shaftColor;
    const center = drawSphere(
        toObj(centerOfMass), sphereRadius, sphereColor,
        shapeOpts
    );

    const head = drawArrow(
        toObj(splitPt), toObj(end),
        radius, HEAD_COLOR, radiusRatio,
        shapeOpts
    );

    return { shaft, center, head };
}

function dipoleTooltipInfo(dipoleInfo, params) {
    const {
        group, label, magnitude, isAggregate,
    } = dipoleInfo;
    const groupLabel = isAggregate
        ? 'Binding pocket'
        : getFullResidueId(group.getAtoms()[0]);
    const strengthLabel = simpleCond([
        [magnitude >= params.strongThreshold, 'Strong'],
        [magnitude >= params.moderateThreshold, 'Moderate'],
        [true, 'Weak'],
    ]);

    const title = label
        ? `Dipole Moment (${label})`
        : 'Dipole Moment';

    return {
        type: 'table',
        title,
        body: [
            ['Group', groupLabel],
            ['Magnitude', `${magnitude.toFixed(2)} D`],
            ['Strength', strengthLabel],
        ],
    };
}
